import { Directive, OnDestroy, OnInit, inject } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatDatepicker, MatDateRangePicker } from '@angular/material/datepicker';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/** The bounds/filter accessors a datepicker exposes, used to gate the Today button. */
interface DatepickerBounds {
  _getMinDate(): unknown;
  _getMaxDate(): unknown;
  _getDateFilter(): ((date: unknown) => boolean) | null | undefined;
}

/** The internal selection model a range picker exposes, used to detect a completed range. */
interface RangePickerModel {
  _model?: {
    selection?: {
      start: unknown;
      end: unknown;
    };
  };
}

/**
 * Adds a "Today" shortcut button to every Material calendar popup (both single
 * `mat-datepicker` and `mat-date-range-picker`). The button is injected into the
 * bottom-right of the open calendar overlay and selects today when clicked.
 *
 * It is injected as raw DOM rather than via `mat-datepicker-actions` on purpose:
 * registering an actions portal would force Material into an apply/cancel flow
 * (a date click would no longer auto-close the calendar). Plain DOM keeps the
 * normal one-click selection behaviour untouched.
 *
 * Declared/exported by DirectivesModule, which SharedModule re-exports, so it
 * auto-applies wherever a date picker is used.
 */
@Directive({
  // Intentionally matches Material's datepicker element selectors so the button
  // auto-applies to every calendar without tagging each usage.
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'mat-datepicker, mat-date-range-picker',
  standalone: false,
})
export class DatepickerTodayButtonDirective implements OnInit, OnDestroy {
  private readonly singlePicker = inject(MatDatepicker, { optional: true, self: true });
  private readonly rangePicker = inject(MatDateRangePicker, { optional: true, self: true });
  private readonly dateAdapter = inject<DateAdapter<unknown>>(DateAdapter);
  private readonly translate = inject(TranslateService);

  private readonly subscriptions = new Subscription();
  private footer?: HTMLElement;

  private get picker(): MatDatepicker<unknown> | MatDateRangePicker<unknown> | null {
    return this.singlePicker ?? this.rangePicker;
  }

  ngOnInit(): void {
    const picker = this.picker;
    if (!picker) {
      return;
    }
    this.subscriptions.add(picker.openedStream.subscribe(() => this.addTodayButton()));
    this.subscriptions.add(picker.closedStream.subscribe(() => this.removeTodayButton()));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.removeTodayButton();
  }

  private addTodayButton(attemptsLeft = 10): void {
    const picker = this.picker;
    if (!picker || this.footer || !picker.opened) {
      return;
    }
    const calendar = document.getElementById(picker.id);
    const container = calendar?.closest('.mat-datepicker-content-container');
    if (container) {
      this.footer = this.buildFooter();
      container.appendChild(this.footer);
      return;
    }
    // The overlay content is rendered on a later change-detection cycle, so the
    // calendar may not exist yet on the first try — retry on the next macrotask.
    if (attemptsLeft > 0) {
      setTimeout(() => this.addTodayButton(attemptsLeft - 1));
    }
  }

  private removeTodayButton(): void {
    this.footer?.remove();
    this.footer = undefined;
  }

  private buildFooter(): HTMLElement {
    const footer = document.createElement('div');
    footer.style.display = 'flex';
    footer.style.justifyContent = 'flex-end';
    footer.style.padding = '0 8px 8px 8px';
    // The app themes .mat-calendar (not the surrounding .mat-datepicker-content),
    // so match the calendar's background to blend in light and dark mode.
    footer.style.backgroundColor = 'var(--bgCard)';

    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = this.translate.instant('dateRange.today');
    button.style.border = 'none';
    button.style.background = 'transparent';
    button.style.borderRadius = '4px';
    button.style.padding = '6px 12px';
    button.style.font = 'inherit';
    button.style.fontWeight = '500';
    // App-themed accent that adapts to light/dark mode (see variables.scss).
    button.style.color = 'var(--primaryColor, #1976d2)';

    const selectable = this.isTodaySelectable();
    if (selectable) {
      button.style.cursor = 'pointer';
      button.addEventListener('mouseenter', () => (button.style.background = 'rgba(127, 127, 127, 0.12)'));
      button.addEventListener('mouseleave', () => (button.style.background = 'transparent'));
      button.addEventListener('click', () => this.selectToday());
    } else {
      button.disabled = true;
      button.style.cursor = 'default';
      button.style.opacity = '0.38';
    }

    footer.appendChild(button);
    return footer;
  }

  private selectToday(): void {
    const today = this.dateAdapter.today();
    if (this.rangePicker) {
      // Add today as the next range endpoint (start if none chosen yet, otherwise
      // end), exactly like clicking today's cell.
      this.rangePicker.select(today);
      // If today just completed the range (a start was already chosen and today
      // is on or after it), close the calendar like a normal second-date click.
      // Otherwise keep it open so the user can pick the other endpoint.
      if (this.isRangeComplete()) {
        this.rangePicker.close();
      }
    } else {
      this.singlePicker?.select(today);
      this.singlePicker?.close();
    }
  }

  /** Whether the range picker now holds a complete, correctly-ordered range. */
  private isRangeComplete(): boolean {
    const selection = (this.rangePicker as unknown as RangePickerModel)._model?.selection;
    const start = selection?.start;
    const end = selection?.end;
    return start != null && end != null && this.dateAdapter.compareDate(start, end) <= 0;
  }

  /** Whether today falls within the picker's min/max bounds and date filter. */
  private isTodaySelectable(): boolean {
    const picker = this.picker;
    if (!picker) {
      return false;
    }
    // `_getDateFilter` is public on MatDatepicker but protected on
    // MatDateRangePicker, so read the bounds through an explicit shape.
    const bounds = picker as unknown as DatepickerBounds;
    const today = this.dateAdapter.today();
    const min = bounds._getMinDate();
    const max = bounds._getMaxDate();
    const filter = bounds._getDateFilter();
    if (min != null && this.dateAdapter.compareDate(today, min) < 0) {
      return false;
    }
    if (max != null && this.dateAdapter.compareDate(today, max) > 0) {
      return false;
    }
    return !filter || filter(today);
  }
}
